import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Info, MessageCircle, X } from 'lucide-react';
import MiniCalendar from './MiniCalendar';
import { API_BASE } from './lib/runtimeConfig';
import { limiteVentana, textoLimiteVentana } from './ventanaReservas';
import { precioEstancia, minimoDe } from './precioEstancia';

const PropertyBookingCard = ({ property, auth, settings }) => {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adultos, setAdultos] = useState(2);
  const [ninos, setNinos] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [availCheck, setAvailCheck] = useState(null); // null | 'checking' | true | false
  const [showContact, setShowContact] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [sendingContact, setSendingContact] = useState(false);

  // --- LÓGICA DE PRECIO Y NEGOCIO ---
  const precioBase = parseFloat(property.basePrice) || 0;
  const tarifaLimpieza = parseFloat(property.cleaningFee) || 0;
  const cargoExtraPorPersona = parseFloat(property.extraPersonFee) || 0;
  const limitePersonasSinCargo = property.extraPersonLimit ?? 2;
  const edadCorteNinos = property.childAgeLimit || 4;
  const ocupacionMaxima = parseInt(property.guests || 10);

  const huespedesTotales = adultos + ninos;

  // Cada noche vale lo fijado ese día en el calendario; sin fechas se muestra el precio base
  const estancia = precioEstancia(precioBase, property.calendarEvents, checkIn, checkOut, property.temporadas);
  const extraPorNoche = Math.max(0, huespedesTotales - limitePersonasSinCargo) * cargoExtraPorPersona;
  const precioPorNoche = estancia.promedio + extraPorNoche;
  const mismaTarifa = estancia.noches.every(n => n.precio === estancia.noches[0].precio);

  const calcularNoches = () => {
    if (!checkIn || !checkOut) return 0;
    const inicio = new Date(checkIn);
    const fin = new Date(checkOut);
    const diferencia = fin - inicio;
    const dias = diferencia / (1000 * 60 * 60 * 24);
    return dias > 0 ? Math.ceil(dias) : 0;
  };

  const noches = calcularNoches();
  const minNoches = minimoDe(property.minNights, property.temporadas, checkIn);
  const limiteLlegada = limiteVentana(property.bookingWindowMonths);
  const fueraDeVentana = !!limiteLlegada && !!checkIn && checkIn > limiteLlegada;
  const puedeReservar = noches > 0 && noches >= minNoches && huespedesTotales <= ocupacionMaxima && availCheck !== false && !fueraDeVentana;
  const totalHospedaje = estancia.hospedaje + extraPorNoche * estancia.noches.length;
  const totalFinal = totalHospedaje + tarifaLimpieza;

  const today = new Date().toISOString().split('T')[0];

  // Verificar disponibilidad en tiempo real cuando cambian las fechas
  useEffect(() => {
    if (!checkIn || !checkOut) { setAvailCheck(null); return; }
    setAvailCheck('checking');
    fetch(`${API_BASE}/api/reservations/availability?propertyId=${property.id}&checkIn=${checkIn}&checkOut=${checkOut}`)
      .then(r => r.json())
      .then(d => setAvailCheck(d.available === true ? true : false))
      .catch(() => setAvailCheck(null));
  }, [checkIn, checkOut, property.id]);

  const handleOpenContact = () => {
    if (!auth?.isAuth) { navigate('/login'); return; }
    if (!checkIn || !checkOut) { alert('Debes seleccionar las fechas para consultar esta propiedad'); return; }
    setShowContact(true);
  };

  const handleSendContact = async () => {
    if (!contactMessage.trim()) return;
    setSendingContact(true);
    try {
      const r = await fetch(`${API_BASE}/api/messages/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientUsername: auth.username,
          propertyId: property.id,
          checkIn,
          checkOut,
          guests: huespedesTotales,
          totalEstimate: totalFinal,
          message: contactMessage.trim(),
        })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'No se pudo iniciar la conversación');
      setContactMessage('');
      setShowContact(false);
      alert('Mensaje enviado. Puedes continuar la conversación en tu sección Mensajes.');
    } catch (e) {
      alert(e.message);
    } finally {
      setSendingContact(false);
    }
  };

  return (
    <div style={cardContainer} className="property-booking-card">
      <div style={priceHeader}>
        <div>
          <span style={priceAmount}>${precioPorNoche.toLocaleString()}</span>
          <span style={priceLabel}> / noche</span>
        </div>
        {huespedesTotales > 2 && (
          <div style={badgeExtra}>
            <Info size={12} /> Cargo extra incluido
          </div>
        )}
      </div>

      <div style={selectorContainer}>
        <div style={dateGrid}>
          <div style={dateBox}>
            <label style={labelStyle}>LLEGADA</label>
            <input
              type="date"
              value={checkIn}
              min={today}
              max={limiteLlegada || undefined}
              onChange={(e) => { setCheckIn(e.target.value); setCheckOut(''); }}
              style={dateInput}
            />
          </div>
          <div style={{ ...dateBox, borderLeft: '1px solid #ddd' }}>
            <label style={labelStyle}>SALIDA</label>
            <input
              type="date"
              value={checkOut}
              min={checkIn || today}
              onChange={(e) => setCheckOut(e.target.value)}
              style={dateInput}
            />
          </div>
        </div>

        <div style={guestBox}>
          <label style={labelStyle}>ADULTOS</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={14} color="#003366" />
            <select
              value={adultos}
              onChange={(e) => setAdultos(parseInt(e.target.value))}
              style={guestSelect}
            >
              {[...Array(ocupacionMaxima)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1} {i === 0 ? 'adulto' : 'adultos'}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ ...guestBox, borderTop: '1px solid #ddd' }}>
          <label style={labelStyle}>NIÑOS (Pagan desde los {edadCorteNinos + 1} años)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={14} color="#003366" />
            <select
              value={ninos}
              onChange={(e) => setNinos(parseInt(e.target.value))}
              style={guestSelect}
            >
              {[...Array(Math.max(0, ocupacionMaxima - adultos + 1))].map((_, i) => (
                <option key={i} value={i}>
                  {i} {i === 1 ? 'niño' : 'niños'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Toggle calendario visual de disponibilidad */}
      <button onClick={() => setShowCalendar(c => !c)} style={calendarToggleBtn}>
        📅 {showCalendar ? 'Ocultar' : 'Ver'} calendario de disponibilidad
      </button>
      {showCalendar && (
        <div style={calendarBox}>
          <MiniCalendar
            propertyId={property.id}
            checkin={checkIn}
            checkout={checkOut}
            onSelectCheckin={setCheckIn}
            onSelectCheckout={setCheckOut}
            maxCheckin={limiteLlegada}
          />
          {checkIn && !checkOut && <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 0', textAlign: 'center' }}>Ahora selecciona la fecha de salida</p>}
        </div>
      )}

      {limiteLlegada && (
        <p style={{ fontSize: 12, color: fueraDeVentana ? '#dc2626' : '#64748b', fontWeight: fueraDeVentana ? 700 : 400, margin: '0 0 12px', textAlign: 'center' }}>
          Reservas abiertas con llegada hasta el {textoLimiteVentana(limiteLlegada)}
        </p>
      )}

      {/* Estado de disponibilidad en tiempo real */}
      {checkIn && checkOut && (
        <div style={{
          padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13, fontWeight: 700,
          background: availCheck === true ? '#f0fdf4' : availCheck === false ? '#fef2f2' : '#f8fafc',
          color: availCheck === true ? '#16a34a' : availCheck === false ? '#dc2626' : '#64748b',
          border: `1px solid ${availCheck === true ? '#bbf7d0' : availCheck === false ? '#fecaca' : '#e2e8f0'}`,
        }}>
          {availCheck === 'checking' && '🔍 Verificando disponibilidad...'}
          {availCheck === true  && '✅ Disponible para estas fechas'}
          {availCheck === false && '❌ No disponible para estas fechas'}
        </div>
      )}

      {noches > 0 && (
        <div style={calcTable}>
          <div style={calcRow}>
            <span style={calcDescription}>{mismaTarifa ? `$${precioPorNoche.toLocaleString()} x ${noches} noches` : `${noches} noches (promedio $${precioPorNoche.toLocaleString()})`}</span>
            <span style={calcValue}>${totalHospedaje.toLocaleString()}</span>
          </div>
          <div style={calcRow}>
            <span style={calcDescription}>Tarifa de limpieza</span>
            <span style={calcValue}>${tarifaLimpieza.toLocaleString()}</span>
          </div>
          <div style={calcRow}>
            <span style={calcDescription}>Tarifa de servicio</span>
            <span style={{...calcValue, color: '#25D366'}}>¡Incluida!</span>
          </div>
          <div style={totalRow}>
            <span>Total</span>
            <span>${totalFinal.toLocaleString()}</span>
          </div>
        </div>
      )}

      <button
        style={puedeReservar ? { ...reserveBtn, marginTop: '24px' } : { ...disabledBtn, marginTop: '24px' }}
        disabled={!puedeReservar}
        onClick={() => {
          if (puedeReservar) {
            navigate(`/reservar?propertyId=${property.id}&checkin=${checkIn}&checkout=${checkOut}&guests=${huespedesTotales}`);
          }
        }}
      >
        {fueraDeVentana
          ? 'Llegada fuera de las fechas abiertas'
          : noches > 0 && noches < minNoches
          ? `Estadía mínima: ${minNoches} noches`
          : noches > 0
          ? 'Reservar ahora'
          : 'Selecciona tus fechas'}
      </button>

      <button style={contactBtn} onClick={handleOpenContact}>
        <MessageCircle size={15} /> {auth?.isAuth ? 'Contactar al anfitrión' : 'Inicia sesión para contactar'}
      </button>

      {settings?.whatsappContact && (
        <a
          href={`https://wa.me/${settings.whatsappContact.replace(/\D/g, '')}?text=Hola, me interesa ${property.title || property.nickname}`}
          target="_blank" rel="noreferrer"
          style={whatsappBtn}
        >
          💬 Consultar por WhatsApp
        </a>
      )}

      <p style={footerText}>
        Pagas en el siguiente paso y tu reserva queda confirmada al instante
      </p>

      {showContact && (
        <div style={innerOverlay} onClick={() => setShowContact(false)}>
          <div style={contactCard} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 900, color: '#003366', fontSize: 18 }}>Contactar al anfitrión</div>
              <button onClick={() => setShowContact(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.7, marginBottom: 12 }}>
              <strong>{property.nickname || property.title}</strong><br />
              {checkIn} → {checkOut} · {huespedesTotales} personas · ${totalFinal.toLocaleString()}
            </div>
            <textarea
              value={contactMessage}
              onChange={e => setContactMessage(e.target.value)}
              placeholder="Hola, estoy interesado en esta propiedad y quisiera más información."
              style={{ width: '100%', minHeight: 130, borderRadius: 12, border: '1px solid #cbd5e1', padding: 12, boxSizing: 'border-box', resize: 'vertical', outline: 'none', fontSize: 13 }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button onClick={() => setShowContact(false)} style={secondaryMiniBtn}>Cancelar</button>
              <button onClick={handleSendContact} disabled={sendingContact || !contactMessage.trim()} style={primaryMiniBtn}>
                {sendingContact ? 'Enviando...' : 'Enviar mensaje'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- ESTILOS ---
const cardContainer = {
  border: '1px solid #eee',
  borderRadius: '16px',
  padding: '24px',
  boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  position: 'sticky',
  top: '110px'
};

const priceHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' };
const priceAmount = { fontSize: '24px', fontWeight: '800', color: '#003366' };
const priceLabel = { fontSize: '16px', color: '#717171' };

const badgeExtra = {
  fontSize: '11px',
  backgroundColor: '#FFF9E6',
  color: '#DAA520',
  padding: '4px 8px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontWeight: '700',
  border: '1px solid #F3E5AB'
};

const selectorContainer = { border: '1px solid #bbb', borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' };
const dateGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #bbb' };
const dateBox = { padding: '12px' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#222' };
const dateInput = { border: 'none', width: '100%', outline: 'none', fontSize: '13px', marginTop: '5px', cursor: 'pointer', fontWeight: '500' };
const guestBox = { padding: '12px' };
const guestSelect = { border: 'none', width: '100%', outline: 'none', fontSize: '14px', background: 'none', cursor: 'pointer', fontWeight: '500' };

const calendarToggleBtn = { width: '100%', background: 'none', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '8px', cursor: 'pointer', fontSize: 12, color: '#64748b', marginBottom: 12 };
const calendarBox = { background: '#f8fafc', borderRadius: 12, padding: 14, marginBottom: 12, border: '1px solid #e2e8f0' };

const reserveBtn = {
  width: '100%',
  padding: '16px',
  background: '#003366',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '700',
  cursor: 'pointer',
  transition: '0.3s shadow'
};

const disabledBtn = { ...reserveBtn, background: '#ccc', cursor: 'not-allowed' };
const footerText = { textAlign: 'center', fontSize: '12px', color: '#717171', marginTop: '15px' };

const calcTable = { marginTop: '12px' };
const calcRow = { display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' };
const calcDescription = { color: '#444', textDecoration: 'underline' };
const calcValue = { color: '#222', fontWeight: '600' };

const totalRow = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '18px',
  paddingTop: '18px',
  borderTop: '2px solid #f0f0f0',
  fontWeight: '900',
  fontSize: '19px',
  color: '#003366'
};

const contactBtn = { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: '12px', padding: '14px', background: '#fff', color: '#003366', border: '1.5px solid #003366', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' };
const whatsappBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: '12px', padding: '13px', background: '#25D366', color: '#fff', borderRadius: '10px', fontSize: '14px', fontWeight: '700', textDecoration: 'none' };

const innerOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const contactCard = { background: '#fff', borderRadius: 16, padding: 22, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const secondaryMiniBtn = { padding: '10px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13 };
const primaryMiniBtn = { padding: '10px 18px', background: '#003366', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 13 };

export default PropertyBookingCard;
